<template>
  <div class="main">
    <!-- Modal to set add new Restricted Modules -->
    <CustomModal
      v-model="modalMixin.visible"
      :closable="modalMixin.closable"
      :mask-closable="modalMixin.maskClosable"
      :category="modalMixin.category"
      :title="modalMixin.title"
      :message="modalMixin.message"
      :action-buttons="modalMixin.actionButtons"
      :cancel-button="modalMixin.cancelButton"
      :is-form-valid="isFormValid"
      @closeModal="closeActionWindow"
    >
      <template #content>
        <Form
          v-show="modalMixin.showForm"
          ref="usersPrivileges"
          :model="form"
          :rules="formRules"
          class="form"
        >
          <FormItem prop="moduleName">
            <label>Module:</label>
            <Select
              v-model="form.moduleName"
              placeholder="Module where the section to be restricted is located"
            >
              <Option
                v-for="moduleName in modulesList"
                :key="moduleName"
                :label="moduleName"
                :value="moduleName"
              />
            </Select>
          </FormItem>
          <FormItem prop="moduleSection">
            <label>Section:</label>
            <Select
              v-model="form.moduleSection"
              placeholder="Section to be restricted"
            >
              <Option
                v-for="route in routeList.filter(route => route.moduleName === form.moduleName)"
                :key="route.name"
                :label="route.name"
                :value="route.name"
              />
            </Select>
          </FormItem>
          <FormItem
            v-if="isSectionParamRequired"
            prop="moduleParam"
          >
            <label>Required Param:</label>
            <i-input
              v-model="form.moduleParam"
              name="moduleParam"
              placeholder="URL param for the section to be restricted (REQUIRED)"
              clearable
            >
              <span slot="prepend">{{ sectionParamName }}</span>
            </i-input>
          </FormItem>
        </Form>
      </template>
    </CustomModal>

    <!-- Modal to set Users Privileges on Restricted Modules -->
    <CustomModal
      v-model="modal.visible"
      width="704px"
      :closable="modal.closable"
      :mask-closable="modal.maskClosable"
      :title="modal.title"
      :icon="modal.icon"
      :message="modal.message"
      :action-buttons="modal.actionButtons"
      :cancel-button="modal.cancelButton"
      :is-form-valid="true"
      @closeModal="modal.visible = false"
    >
      <template #content>
        <div class="transfer-row">
          <div class="transfer-column">
            Restricted Users
          </div>
          <div class="transfer-column">
            Allowed Users
          </div>
        </div>
        <div class="transfer-row">
          <div class="transfer-column">
            <Collapse
              v-model="emailsList.collapseBlocked"
              class="collapse"
              style="margin: 8px 0"
            >
              <Panel name="blockedList">
                <b>Emails List</b>
                <Form slot="content">
                  <FormItem class="row">
                    <textarea
                      v-model="emailsList.blockedList"
                      placeholder="Paste here the list of emails of the users you want to block."
                      rows="5"
                      class="list-container"
                    />
                  </FormItem>
                  <FormItem class="row">
                    <Button
                      type="warning"
                      class="queryResultButtons"
                      :disabled="!emailsList.blockedList"
                      @click="validateEmailsList(emailsList.blockedList)"
                    >
                      Block Emails
                    </Button>
                  </FormItem>
                </Form>
              </Panel>
            </Collapse>
          </div>
          <div class="transfer-column">
            <Collapse
              v-model="emailsList.collapseAllowed"
              class="collapse"
              style="margin: 8px 0"
            >
              <Panel name="allowedList">
                <b>Emails List</b>
                <Form slot="content">
                  <FormItem class="row">
                    <textarea
                      v-model="emailsList.allowedList"
                      placeholder="Paste here the list of emails of the users you want to allow."
                      rows="5"
                      class="list-container"
                    />
                  </FormItem>
                  <FormItem class="row">
                    <Button
                      type="success"
                      class="queryResultButtons"
                      :disabled="!emailsList.allowedList"
                      @click="validateEmailsList(emailsList.allowedList, false)"
                    >
                      Allow Emails
                    </Button>
                  </FormItem>
                </Form>
              </Panel>
            </Collapse>
          </div>
        </div>
        <Transfer
          :data="transferUsersData"
          :target-keys="transfer.targetKeys"
          :list-style="transfer.listStyle"
          :titles="['Select All', 'Select All']"
          :operations="['Remove','Grant']"
          filter-placeholder="Search user"
          not-found-text="User not found"
          filterable
          @on-change="updateAllowedUsers"
        />
      </template>
    </CustomModal>

    <div class="title">
      <h2>{{ title }}</h2>
      <AdminToolBar
        show-add
        :is-filtered="isFiltered"
        @onAddItem="openAddRestrictedModuleForm"
      />
    </div>
    <div
      ref="content"
      class="content"
    >
      <Spin
        v-if="loading"
        fix
      >
        <Icon
          type="ios-loading"
          size="18"
          class="spin"
        />
        <div>Loading</div>
      </Spin>
      <template v-else>
        <div
          v-if="!list.length"
          class="no-data"
        >
          There isn't any registered Restricted Module.
        </div>
        <Table
          v-else
          :width="contentWidth"
          :max-height="contentHeight"
          :columns="columns"
          :data="list"
          class="table"
        >
          <template
            slot="totalUsers"
            slot-scope="{ row }"
          >
            {{ row.allowedUsers.length }}
          </template>
          <template
            slot="actions"
            slot-scope="{ row }"
          >
            <i
              class="fas fa-user-edit action-icon"
              @click="setUpUsersPrivileges(row.id)"
            />
            <!-- This code will be reactivated later -->
            <!-- <i
              class="fas fa-edit action-icon"
              @click="openEditRestrictedModuleForm(row.id)"
            /> -->
            <!-- <i
              class="fas fa-trash-alt action-icon"
              @click="onDeleteRestrictedModule(row.id)"
            /> -->
          </template>
        </Table>
      </template>
    </div>
  </div>
</template>

<script>
import CustomModal from '../CustomModal'
import AdminToolBar from './AdminToolBar'
import { customModalMixin } from '@/mixins/customModalMixin'
import UserMixin from '@/mixins/UserMixin'
import { SUCCESS, WARNING, DEFAULT } from '@/constants/buttons'
import axios from 'axios'
import _ from 'lodash'

export default {
  components: {
    CustomModal,
    AdminToolBar
  },

  mixins: [
    customModalMixin,
    UserMixin
  ],

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      contentWidth: 0,
      contentHeight: 0,
      loading: true,
      lastId: 0,
      list: [],
      selectedModule: {},
      columns: [
        {
          title: 'Module',
          key: 'moduleName',
          sortable: true,
          sortType: 'asc'
        },
        {
          title: 'Section',
          key: 'moduleSection',
          sortable: true
        },
        {
          title: 'Param',
          key: 'moduleParam',
          width: '160',
          sortable: true
        },
        {
          title: 'Allowed Users',
          slot: 'totalUsers',
          width: '136',
          align: 'center',
          sortable: true
        },
        {
          title: 'Actions',
          slot: 'actions',
          width: '112',
          align: 'center'
        }
      ],
      form: {
        moduleName: '',
        moduleSection: '',
        moduleParam: ''
      },
      formRules: {
        moduleName: [
          { type: 'string', required: true, message: 'Module cannot be empty', trigger: ['change'] }
        ],
        moduleSection: [
          { type: 'string', required: true, message: 'Section cannot be empty', trigger: ['change'] }
        ],
        moduleParam: [
          { validator: this.validateModuleParam, trigger: ['blur', 'change'] }
        ]
      },
      filters: {
        status: 'all'
      },
      modal: {
        visible: false,
        closable: true,
        maskClosable: false,
        title: '',
        icon: 'fas fa-user-edit',
        message: '',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: () => this.onSaveUsersPrivileges(),
            restrictedByFormRules: true
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        autoClose: 0
      },
      transfer: {
        targetKeys: [],
        listStyle: {
          width: '280px',
          height: '400px'
        }
      },
      emailsList: {
        users: null,
        selectedUsers: null,
        collapseBlocked: '',
        blockedList: '',
        collapseAllowed: '',
        allowedList: ''
      },
      isRemoveAction: null
    }
  },

  computed: {
    /**
     * Verifies is the dashboards form is valid (all required fields have been populated) or not.
     */
    isFormValid () {
      const { moduleName = '', moduleSection = '', moduleParam } = this.form
      return moduleName.length > 0 && moduleSection.length > 0 &&
        (!this.isSectionParamRequired || moduleParam.length > 0)
    },

    /**
     * Verifies if the dashboards list is using a filter from the toolbar.
     */
    isFiltered () {
      return !this.filters.keywords
    },

    /**
     * Gets the original list of users to be used in the Transfer component.
     */
    transferUsersData () {
      if (!(this.emailsList.users || []).length) {
        return []
      }

      return this.emailsList.users.map(email => {
        return {
          key: email.toLowerCase(),
          label: email.toLowerCase()
        }
      })
    },

    /**
     * Fetches the list of the existing routes within the Lime Internal.
     */
    routeList () {
      return this.createRouteModuleList(this.$router.options.routes)
    },

    /**
     * Fetches the list of the existin gmodules within the Lime Internal.
     */
    modulesList () {
      if (!this.routeList) {
        return []
      }

      const modules = {}
      this.routeList.forEach(route => {
        if (!modules[route.moduleName]) {
          modules[route.moduleName] = true
        }
      })

      return Object.keys(modules).map(item => item)
    },

    /**
     * Verifies if the Param is required for the selected Module.
     */
    isSectionParamRequired () {
      const sectionName = this.form.moduleSection
      if (!sectionName) {
        return false
      }
      return this.routeList.find(route => route.name === sectionName && route.path.includes('/:'))
    },

    /**
     * Fetches the list of the Params related to the selected Module.
     */
    sectionParamName () {
      return this.isSectionParamRequired.path.split(':').pop()
    }
  },

  async mounted () {
    window.addEventListener('resize', this.getContentMeasures)

    if (!this.users.length) {
      await this.fetchAllUsers()
    }
    this.setUsersList()

    this.getContentMeasures()
  },

  methods: {
    /**
     * Gets the height of the content according with the browser height.
     */
    getContentMeasures () {
      if (this.$refs.content) {
        this.contentWidth = this.$refs.content.offsetWidth
        this.contentHeight = this.$refs.content.offsetHeight
      }
    },

    /**
     * Sets the users list.
     */
    setUsersList () {
      const users = this.users.map(user => {
        return user.email
      })
      this.$set(this.emailsList, 'users', users)
      this.fetchRestrictedModules()
    },

    /**
     * Opens a modal to add a new Restricted Module.
     */
    openAddRestrictedModuleForm () {
      // Initialize the form params.
      this.resetUsersPrivilegesForm()

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Add Restricted Module',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: () => this.onSaveRestrictedModule(),
            restrictedByFormRules: true
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: true
      }
      this.showModalMixin(modalData)
    },

    /**
     * Fetches the existing Restricted Modules.
     */
    async fetchRestrictedModules () {
      this.loading = true

      try {
        const modulesList = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/restricted-modules`)).data
        const lastItem = modulesList[modulesList.length - 1]
        // TODO: clean up the duplicated data on S3 (duplicated allowed users), now use Set here to dedup
        this.list = modulesList
        this.lastId = lastItem ? lastItem.id : 0

        // Add the not registered users into the users list.
        modulesList.forEach(moduleData => {
          this.$set(this.emailsList, 'users', Array.from(new Set([...this.emailsList.users, ...moduleData.allowedUsers])))
        })
      } catch (error) {
        const content = `Failed to fetch Restricted Modules: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }

      this.loading = false
    },

    /**
     * Opens a form in order to edit a Restricted Module and it's allowed users (if applies).
     * @param {Number} id
     */
    openEditRestrictedModuleForm (id) {
      this.selectedModule = this.list.find(item => item.id === id)

      // Initialize the form params.
      this.$set(this.form, 'moduleName', this.selectedModule.moduleName)
      this.$set(this.form, 'moduleSection', this.selectedModule.moduleSection)
      this.$set(this.form, 'moduleParam', this.selectedModule.moduleParam)

      // Set up the modal properties and display the modal.
      const modalData = {
        title: 'Edit Restricted Module',
        actionButtons: [
          {
            label: 'Save',
            type: SUCCESS,
            callback: () => this.onUpdateRestrictedModule(),
            restrictedByFormRules: true
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: true
      }
      this.showModalMixin(modalData)
    },

    /**
     * Resets the Users Privileges form fields.
     */
    resetUsersPrivilegesForm () {
      this.$refs['usersPrivileges'].resetFields()
      this.form.moduleName = ''
      this.form.moduleSection = ''
      this.form.moduleParam = ''
      this.$set(this.modal, 'visible', false)
      this.closeActionWindow()
    },

    /**
     * Creates a new Restricted Module on "Users Privileges" section.
     */
    async onSaveRestrictedModule () {
      const {
        moduleName,
        moduleSection,
        moduleParam
      } = this.form
      const newModule = {
        id: this.lastId + 1,
        moduleName,
        moduleSection,
        moduleParam,
        allowedUsers: []
      }
      this.list.push(newModule)
      this.lastId = this.list.length

      try {
        await axios.post(`${process.env.VUE_APP_API_URI}/admin-console/restricted-module`, {
          item: newModule
        })

        this.$Message.info({
          content: `Module added into Users Privileges successfully!`,
          duration: 5
        })
      } catch (error) {
        const content = `Failed to create the new Module into Users Privileges: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }

      this.resetUsersPrivilegesForm()
    },

    /**
     * Updates a Restricted Module on "Users Privileges" section.
     */
    async onUpdateRestrictedModule () {
      const selectedModule = this.selectedModule
      selectedModule.allowedUsers = Array.from(new Set(selectedModule.allowedUsers))
      const {
        moduleName,
        moduleSection,
        moduleParam
      } = this.form
      try {
        if (moduleName) {
          selectedModule.moduleName = moduleName
          selectedModule.moduleSection = moduleSection
          selectedModule.moduleParam = moduleParam
        }

        await axios.put(`${process.env.VUE_APP_API_URI}/admin-console/restricted-module`, {
          item: selectedModule
        })

        this.$Message.info({
          content: `Users Privileges has been updated successfully!`,
          duration: 5
        })
      } catch (error) {
        const content = `Failed to update Users Privileges: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }

      this.resetUsersPrivilegesForm()
    },

    /**
     * Opens a pop-up window to allow the user setup which users will be allowed to access to the selected module.
     * @param {Number} id  Restricted Module ID
     */
    setUpUsersPrivileges (id) {
      const selectedModule = this.list.find(item => item.id === id)
      this.$set(this.emailsList, 'collapseBlocked', '')
      this.$set(this.emailsList, 'collapseAllowed', '')
      this.selectedModule = selectedModule
      this.$set(this.modal, 'title', `Setup Users Privileges for ${selectedModule.moduleName}`)
      this.$set(this.modal, 'visible', true)
      this.getAllowedUsersKeys()
    },

    /**
     * Fetches the keys for the allowed users from a restricted module compared with the current users in Lime Internal.
     * NOTE: This function is used to setup the "targetKey" for the "Transfer" component.
     */
    getAllowedUsersKeys () {
      const allowedUsers = new Set()
      this.selectedModule.allowedUsers.forEach(user => {
        const userFound = this.transferUsersData.find(item => item.label === user)
        if (userFound) {
          allowedUsers.add(userFound.key)
        }
      })
      this.$set(this.transfer, 'targetKeys', Array.from(allowedUsers).sort())
    },

    /**
     * Updates the Allowed Users list when they are added or removed
     * @param {Array} newTargetKeys
     */
    updateAllowedUsers (newTargetKeys) {
      this.transfer.targetKeys = newTargetKeys.sort()
    },

    /**
     * Saves the allowed users of the selected module.
     */
    onSaveUsersPrivileges () {
      const allowedUsers = this.transfer.targetKeys.map(email => email).sort()

      if (!allowedUsers.length) {
        return
      }

      this.$set(this.selectedModule, 'allowedUsers', allowedUsers)
      this.onUpdateRestrictedModule()
    },

    /**
     * Displays a pop-up window to confirm if the user wants to remove a module.
     * @param {Number} id
     */
    onDeleteRestrictedModule (id) {
      this.selectedModule = this.list.find(item => item.id === id)
      const modalData = {
        title: `Delete Restricted Module`,
        category: 'warning',
        message: `Do you confirm to delete ${this.selectedModule.moduleSection} section?`,
        actionButtons: [
          {
            label: 'Confirm',
            type: WARNING,
            callback: () => { this.deleteRestrictedModule() }
          }
        ],
        cancelButton: {
          label: 'Cancel'
        },
        showForm: false
      }
      this.showModalMixin(modalData)
    },

    /**
     * Deletes a Restricted Module.
     */
    async deleteRestrictedModule () {
      this.closeActionWindow()
      const selectedModule = this.selectedModule

      try {
        await axios.delete(`${process.env.VUE_APP_API_URI}/admin-console/restricted-module`, {
          params: {
            itemId: selectedModule.id
          }
        })

        this.$Message.info({
          content: `${selectedModule.moduleSection} has been deleted successfully!`,
          duration: 5
        })

        this.fetchRestrictedModules()
      } catch (error) {
        const content = `Failed to delete ${selectedModule.moduleSection}: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      }
    },

    /**
     * Validates the Required Param of a specific Section to be blocked.
     * NOTE: The required param will be evaluated only if the section requires it.
     * @param {Object} rule
     * @param {String} value
     * @param {Function} callback
     */
    validateModuleParam (rule, value, callback) {
      if (this.isSectionParamRequired && !value) {
        callback(new Error('Param value cannot be empty'))
      } else {
        callback()
      }
    },

    /**
     * Validates the given emails in order to check if there's any unexisting email that need to be added into DB
     * @param {Array} emailsList List of emails to allow/block.
     * @param {Boolean} isRemoveAction Specifies if the emails list is for removing or not.
     */
    validateEmailsList (emailsList, isRemoveAction = true) {
      const unexistingEmails = {
        limeEmails: [],
        externalEmails: []
      }

      this.emailsList.selectedUsers = _.clone(emailsList)
      this.isRemoveAction = isRemoveAction

      // Check the emails list trying to identify unexisting emails.
      // NOTE: The list can handle 'newline', semicolon (';') or comma (',') as separator.
      emailsList.split(/\n|,|;/)
        .forEach(email => {
          email = email.trim()
          const emailDomain = email.split('@').pop()

          // Verify if the email exists into the existing Users list.
          const emailIndex = this.transferUsersData.findIndex(item => item.label === email.toLowerCase())

          // If the email doesn't exist, add it into the proper array: 'limeEmails' or 'externalEmails'.
          if (emailIndex < 0) {
            const emailsArray = ['li.me', 'ext.li.me'].includes(emailDomain)
              ? unexistingEmails.limeEmails
              : unexistingEmails.externalEmails
            emailsArray.push(email)
          }
        })

      if (!isRemoveAction) {
        // Clean and close the "allowedList" item.
        this.$set(this.emailsList, 'allowedList', '')
        this.$set(this.emailsList, 'collapseAllowed', '')
      } else {
        // Clean and close the "blockedList" item.
        this.$set(this.emailsList, 'blockedList', '')
        this.$set(this.emailsList, 'collapseBlocked', '')
      }

      // Display the emails not found if there's any. Otherwise, update the emails list.
      if (!_.isEmpty(unexistingEmails)) {
        this.evaluateLimeEmails(unexistingEmails)
      } else {
        this.updateEmailsList(emailsList, isRemoveAction)
      }
    },

    /**
     * Displays a pop-up message with the list of the emails not found in a given list.
     * @param {Object} unexistingEmails
     */
    evaluateLimeEmails (unexistingEmails) {
      const limeEmails = unexistingEmails.limeEmails || []

      if (!limeEmails.length || this.isRemoveAction) {
        this.evaluateExternalEmails(unexistingEmails.externalEmails)
      } else {
        const limeEmailBullets = unexistingEmails.limeEmails.reduce((acc, email) => `${acc}<li>${email}</li>`, '')
        const limeEmailMessage = `
          The following lime emails don't exist in limeinternal.com:<br>
          <ul style="padding-left: 32px;">${limeEmailBullets}</ul>
          Do you want to add them?
        `

        const modalData = {
          title: `Emails not found`,
          category: 'warning',
          message: limeEmailMessage.trim(),
          actionButtons: [{
            label: 'Confirm',
            type: SUCCESS,
            callback: () => {
              this.addMissingEmails(unexistingEmails)
            }
          }, {
            label: 'Ignore',
            type: DEFAULT,
            callback: () => {
              this.evaluateExternalEmails(unexistingEmails.externalEmails)
            }
          }]
        }
        this.showModalMixin(modalData)
      }
    },

    /**
     * Creates the unexisting Lime emails into DB.
     * @param {Object} unexistingEmails
     */
    async addMissingEmails (unexistingEmails) {
      const { limeEmails, externalEmails } = unexistingEmails
      try {
        // Add the new user into the list of users.
        this.$set(this.emailsList, 'users', [...this.emailsList.users, ...limeEmails].sort())
      } catch (error) {
        const content = `Failed to add new Lime users: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
      } finally {
        this.evaluateExternalEmails(externalEmails)
      }
    },

    /**
     * Displays a pop-up message with the list of all the on-Lime emails (if there's any).
     * @param {Array} externalEmails
     */
    evaluateExternalEmails (externalEmails) {
      if (!externalEmails.length) {
        this.closeActionWindow()
      } else {
        const externalEmailBullets = externalEmails.reduce((acc, email) => `${acc}<li>${email}</li>`, '')
        const limeExternalMessage = `
          The following emails don't belong to a valid Lime email and were ignored:<br>
          <ul style="padding-left: 32px;">${externalEmailBullets}</ul>
        `

        const modalData = {
          title: `Discarded Emails`,
          category: 'warning',
          message: limeExternalMessage.trim(),
          cancelButton: {
            label: 'OK'
          }
        }
        this.showModalMixin(modalData)
      }

      this.updateEmailsList(this.emailsList.selectedUsers, this.isRemoveAction)
    },

    /**
     * Adds/Removes a group of users from the Allowed Users list.
     * @param {Array} emailsList List of emails to allow/block.
     * @param {Boolean} isRemoveAction Specifies if the emails list is for removing or not.
     */
    updateEmailsList (emailsList, isRemoveAction = true) {
      const targetKeys = this.transfer.targetKeys

      // Check the list sent trying to get only the registered emails from the existing users list.
      // NOTE: The list can handle 'newline', semicolon (';') or comma (',') as separator.
      emailsList.split(/\n|,|;/)
        .forEach(email => {
          email = email.toLowerCase().trim()

          // Verify if the user exist in the whole data pool (transferUserData)
          const user = this.transferUsersData.find(item => item.key === email)

          if (user) {
            // Verify if the email index exists in the Allowed Users list.
            const allowedIndex = targetKeys.findIndex(userEmail => userEmail === user.key)

            /**
             * If the "emailsList" belongs to the emails to add and the email index doesn't exist, add it.
             * Otherwise, if the list belongs to the emails to block and the email index exists, remove it.
             */
            if (!isRemoveAction && allowedIndex < 0) {
              targetKeys.push(user.key)
            } else if (isRemoveAction && allowedIndex >= 0) {
              targetKeys.splice(allowedIndex, 1)
            }
          }
        })

      // Update the Allowed Users list.
      this.updateAllowedUsers(targetKeys)
    },

    /**
     * Closes the action window (modal).
     */
    closeActionWindow () {
      this.hideModalMixin()
    },

    createRouteModuleList (routes) {
      const routeModules = []

      routes.forEach(route => {
        if (route.children) {
          routeModules.push(...this.createRouteModuleList(route.children))
        }

        if (!route.redirect && route.meta.module) {
          const { path, name, meta } = route

          routeModules.push({
            path,
            name,
            moduleName: meta.module
          })
        }
      })

      return routeModules
    }
  }
}
</script>

<style scoped>
  .main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .spin,
  .table >>> .ivu-table-cell .spin {
    animation: ani-demo-spin 1s linear infinite;
  }

  .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .title .actions {
    width: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .no-data {
    font-size: 24px;
    color: var(--color-gray);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-icon {
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-black);
    cursor: pointer;
  }

  .form >>> .ivu-form-item-content {
    display: flex;
    flex-direction: column;
  }

  .table >>> .ivu-table-cell {
    padding: 0 8px;
  }

  .table >>> .ivu-table-cell .fas {
    font-size: 16px;
  }

  .table >>> .ivu-table-cell .action-icon {
    cursor: pointer;
  }

  .table >>> .ivu-table-cell .action-icon:not(:first-child) {
    margin-left: 8px;
  }

  .table >>> .ivu-table-cell .action-icon.hidden {
    display: none;
  }

  .table >>> .ivu-table-cell .fas.fa-exclamation-circle {
    color: var(--color-yellow-dark);
  }

  .table >>> .ivu-table-cell .fas.fa-check-circle {
    color: var(--color-green-lime);
  }

  .table >>> .ivu-table-cell .fas.fa-ban {
    color: var(--color-red);
  }

  .transfer-row {
    position: relative;
    display: flex;
    flex-direction: row;
  }

  .transfer-row .transfer-column {
    width: 280px;
    font-weight: bold;
  }

  .transfer-row .transfer-column:first-child {
    margin-right: 108px;
  }

  .transfer-row .transfer-column .row {
    margin: 0;
  }

  .transfer-row .transfer-column .row >>> .ivu-form-item-content {
    line-height: unset;
  }

  .transfer-row .transfer-column .list-container {
    margin-bottom: 8px;
    padding: 8px;
    width: 100%;
    border: 1px solid var(--color-gray-border);
    resize: none;
    line-height: normal;
  }

  .transfer-row .transfer-column .list-container:focus {
    outline: none;
  }
</style>
